import { useContext } from 'react';

import { IContextMenuItem } from '@smartshell/ui-kit';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import { LocalizationContext } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';
import { SettingsContext } from 'settings';
import { TGood, TGroup } from 'types';

import s from './ContextMenuItems.module.scss';
import {
  IUseContextMenuCategoryArgs,
  IUseContextMenuGoodsArgs,
} from './interfaces';

export const useContextMenuItemsGoods = ({
  openAmountEditor,
  deleteGood,
  show_in_shell,
  isGroup,
  toggleShowInShell,
  loadedCategories,
  updateGood,
  selectedGoods,
  deleteGoods,
  updateGoods,
  setDeletingMessage,
  setAmountReport,
}: IUseContextMenuGoodsArgs): Array<IContextMenuItem<TGood[]>> => {
  const { t } = useContext(LocalizationContext);

  const {
    state: {
      settings: { telegramChannelId },
    },
  } = useContext(SettingsContext);
  const navigate = useNavigate();

  const baseArray: Array<IContextMenuItem<TGood[]>> = [
    {
      key: 'warehouse',
      text: t('good.amount.arriving'),
      iconName: 'BarCodeIcon',
      className: selectedGoods.length !== 1 ? s.disabled : undefined,
      onClick: (good) => {
        openAmountEditor(good[0]);
      },
    },
    {
      key: 'warehouse_report',
      text: t('goods.amount.report.context_2'),
      iconName: 'TimeForwardIcon',
      className: selectedGoods.length !== 1 ? s.disabled : undefined,
      onClick: (good) => {
        setAmountReport(good[0]);
      },
    },
    {
      key: 'edit',
      text: t('edit'),
      iconName: 'EditIcon',
      className: selectedGoods.length !== 1 ? s.disabled : undefined,
      onClick: (good) => {
        navigate(`/goods/${good[0].id}`);
      },
    },
    {
      key: 'low_stock_notifications',
      text: t('goods.context.telegram'),
      iconName: 'TelegramBellIcon',
      disabled: !telegramChannelId?.id,
      subMenu: [
        {
          key: 'on',
          className: selectedGoods.every(
            (item) => item.low_stock_notification?.enabled
          )
            ? s.disabled
            : '',
          text: t('host.ctx.on'),
          iconName: 'ShutdownIcon',
          onClick: (goods: TGood[]) => {
            if (selectedGoods.length === 1) {
              if (selectedGoods[0].in_combo) {
                const ids = goods.map((good) => good.id);

                return updateGoods({
                  ids,
                  input: { low_stock_notification: { enabled: true } },
                });
              }
              return updateGood({
                ...selectedGoods[0],
                low_stock_notification: { enabled: true },
              });
            }
            if (selectedGoods.length > 1) {
              const ids = goods.map((good) => good.id);
              updateGoods({
                ids,
                input: { low_stock_notification: { enabled: true } },
              });
            }
          },
        },
        {
          key: 'off',
          className: selectedGoods.every(
            (item) => !item.low_stock_notification?.enabled
          )
            ? s.disabled
            : '',
          text: t('off_2'),
          iconName: 'CancelIcon',
          onClick: (goods: TGood[]) => {
            if (selectedGoods.length === 1) {
              if (selectedGoods[0].in_combo) {
                const ids = goods.map((good) => good.id);

                return updateGoods({
                  ids,
                  input: { low_stock_notification: { enabled: false } },
                });
              }
              return updateGood({
                ...selectedGoods[0],
                low_stock_notification: { enabled: false },
              });
            }
            if (selectedGoods.length > 1) {
              const ids = goods.map((good) => good.id);
              updateGoods({
                ids,
                input: { low_stock_notification: { enabled: false } },
              });
            }
          },
        },
      ],
    },
    {
      key: 'add_in_group',
      text: t('clients.group.add'),
      iconName: 'CirclePlusIcon',
      className: loadedCategories.length === 0 ? s.disabled : '',
      subMenu: !loadedCategories
        ? []
        : loadedCategories.map((category) => ({
            key: String(category.id),
            text: category.title,
            className: cn(
              (selectedGoods.length === 1 && isGroup?.id === category.id) ||
                selectedGoods.every((item) => item.category?.id === category.id)
                ? s.disabled
                : '',
              s.groups
            ),
            onClick: (goods: TGood[]) => {
              if (
                selectedGoods.length === 1 &&
                goods[0]?.category?.id !== category.id
              ) {
                if (goods[0].in_combo) {
                  if (selectedGoods[0].in_combo) {
                    const ids = goods.map((good) => good.id);

                    return updateGoods({
                      ids,
                      input: { category_id: category.id },
                    });
                  }
                }
                return updateGood({
                  ...goods[0],
                  category: category,
                });
              } else {
                const ids = goods.map((good) => good.id);
                updateGoods({ ids, input: { category_id: category.id } });
              }
            },
          })),
    },
    {
      key: 'delete_from_group',
      text: t('clients.group.remove'),
      iconName: 'CancelIcon',
      className:
        !isGroup && selectedGoods.every((item) => !item.category)
          ? s.disabled
          : '',
      onClick: (goods: TGood[]) => {
        if (selectedGoods.length === 1 && goods[0].category) {
          if (selectedGoods[0].in_combo) {
            const ids = goods.map((good) => good.id);

            return updateGoods({ ids, input: { category_id: 0 } });
          }
          return updateGood({
            ...goods[0],
            category: null,
          });
        } else {
          const ids = goods.map((good) => good.id);
          updateGoods({ ids, input: { category_id: 0 } });
        }
      },
    },
    {
      key: 'delete',
      className: s['remove-item'],
      text: t('delete'),
      iconName: 'BucketIcon',
      onClick: (goods) => {
        if (selectedGoods.length === 1) {
          openConfirmDialog(t, 'remove', {
            title: t('good.deleting'),
            message: t('good.deleting.confirm', goods[0].title),
          }).then(({ status }) => {
            if (status === 'ACCEPTED') {
              deleteGood(goods[0].id);
              if (setDeletingMessage) {
                goods[0].amount !== 0
                  ? setDeletingMessage(t('goods.error.delete.modal'))
                  : setDeletingMessage(t('goods.error.delete.modal_2'));
              }
            }
          });
        }

        if (selectedGoods.length > 1) {
          openConfirmDialog(t, 'remove', {
            title: t('good.deleting'),
            message: t(
              'good.deleting.confirm_double',
              goods.map((good) => good.title).join(', ')
            ),
          }).then(({ status }) => {
            if (status === 'ACCEPTED') {
              const ids = goods.map((good) => good.id);
              deleteGoods(ids);
              if (setDeletingMessage) {
                goods.some((good) => good.amount !== 0)
                  ? setDeletingMessage(t('goods.error.delete.modal'))
                  : setDeletingMessage(t('goods.error.delete.modal_2'));
              }
            }
          });
        }
      },
    },
  ];

  const showShellItems = {
    key: 'show_in_shell',
    text: t('goods.show_in_shell.label'),
    iconName: 'OpenedEyeIcon',
    subMenu: [
      {
        key: 'on',
        className:
          selectedGoods.every((item) => item.show_in_shell) && show_in_shell
            ? s.disabled
            : '',
        text: t('host.ctx.on'),
        iconName: 'ShutdownIcon',
        onClick: (goods: TGood[]) => {
          if (selectedGoods.length === 1) {
            if (selectedGoods[0].in_combo) {
              const ids = goods.map((good) => good.id);

              return updateGoods({ ids, input: { show_in_shell: true } });
            }
            return toggleShowInShell({
              ...selectedGoods[0],
              show_in_shell: true,
            });
          }
          if (selectedGoods.length > 1) {
            const ids = goods.map((good) => good.id);
            updateGoods({ ids, input: { show_in_shell: true } });
          }
        },
      },
      {
        key: 'off',
        className:
          selectedGoods.every((item) => !item.show_in_shell) && !show_in_shell
            ? s.disabled
            : '',
        text: t('off_2'),
        iconName: 'CancelIcon',
        onClick: (goods: TGood[]) => {
          if (selectedGoods.length === 1) {
            if (selectedGoods[0].in_combo) {
              const ids = goods.map((good) => good.id);

              return updateGoods({ ids, input: { show_in_shell: false } });
            }
            return toggleShowInShell({
              ...selectedGoods[0],
              show_in_shell: false,
            });
          }
          if (selectedGoods.length > 1) {
            const ids = goods.map((good) => good.id);
            updateGoods({ ids, input: { show_in_shell: false } });
          }
        },
      },
    ],
  };

  baseArray.splice(2, 0, showShellItems);

  return baseArray;
};

export const useContextMenuItemsCategory = ({
  editGroup,
  show_in_shell,
  deleteCategory,
  toggleShowInShellCategory,
}: IUseContextMenuCategoryArgs): Array<IContextMenuItem<TGroup>> => {
  const { t } = useContext(LocalizationContext);

  const baseArray: Array<IContextMenuItem<TGroup>> = [
    {
      key: 'edit',
      text: t('edit'),
      iconName: 'EditIcon',
      onClick: (group: TGroup) => {
        editGroup(group);
      },
    },
    {
      key: 'delete',
      className: s['remove-item'],
      text: t('delete'),
      iconName: 'BucketIcon',
      onClick: ({ id, title }: { id: number; title: string }) =>
        openConfirmDialog(t, 'remove', {
          title: t('accounts.group.delete.confirm'),
          message: `${t('group.context.delete.message')} ${title}`,
        }).then(({ status }) => {
          if (status === 'ACCEPTED') {
            return deleteCategory(id);
          }
        }),
    },
  ];

  const showShellItems = {
    key: 'show_in_shell',
    text: t('goods.show_in_shell.label'),
    iconName: 'OpenedEyeIcon',
    subMenu: [
      {
        key: 'on',
        className: show_in_shell ? s.disabled : '',
        text: t('host.ctx.on'),
        iconName: 'ShutdownIcon',
        onClick: (group: TGroup) => {
          toggleShowInShellCategory(group);
        },
      },
      {
        key: 'off',
        className: !show_in_shell ? s.disabled : '',
        text: t('off_2'),
        iconName: 'CancelIcon',
        onClick: (group: TGroup) => {
          toggleShowInShellCategory(group);
        },
      },
    ],
  };

  baseArray.splice(1, 0, showShellItems);

  return baseArray;
};
